import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import CarrierFormDialog from './CarrierFormDialog';
import type { CarrierFormValues } from './CarrierFormDialog';
import type { CarrierDto, CreateCarrierRequest, UpdateCarrierRequest } from '@/types/materials';

// Gated on Material.View/Material.Manage rather than a dedicated Carrier permission code —
// mirrors CarriersController.cs on the backend exactly (Carrier piggybacks on the same
// permission pair Category/UOM-style supporting masters use, not its own code).
export default function CarrierMaster() {
  const { t } = useTranslation('carrierMaster');
  const queryClient = useQueryClient();

  const { data: carriers = [], isLoading } = useQuery({
    queryKey: ['carriers'],
    queryFn: async () => (await apiClient.get<CarrierDto[]>('/carriers')).data,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCarrier, setEditingCarrier] = useState<CarrierDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CarrierDto | null>(null);

  const saveMutation = useMutation({
    mutationFn: (values: CarrierFormValues) => {
      if (editingCarrier) {
        const payload: UpdateCarrierRequest = { code: values.code, name: values.name, status: values.status };
        return apiClient.put(`/carriers/${editingCarrier.id}`, payload);
      }
      const payload: CreateCarrierRequest = { code: values.code, name: values.name };
      return apiClient.post('/carriers', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      setDialogOpen(false);
      setEditingCarrier(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/carriers/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['carriers'] });
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<CarrierDto>[] = [
    { field: 'code', headerName: t('table.code') },
    { field: 'name', headerName: t('table.name'), filterable: true },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.MATERIAL_MANAGE}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => {
                setEditingCarrier(r);
                setDialogOpen(true);
              }}
            >
              {t('edit')}
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              {t('delete')}
            </Button>
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.MATERIAL_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingCarrier(null);
                setDialogOpen(true);
              }}
            >
              {t('addCarrier')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={carriers} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="carriers" />

      <CarrierFormDialog
        open={dialogOpen}
        carrier={editingCarrier}
        onClose={() => {
          setDialogOpen(false);
          setEditingCarrier(null);
        }}
        onSubmit={(values) => saveMutation.mutate(values)}
        loading={saveMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteCarrier.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
